import {getDailyToken} from "@/ui/credits/CreditStore"

// API keys are injected server-side by the Vite proxy — never exposed to the client
const REPLICATE_PROXY = "/api/replicate"

// Community models need version hash via /v1/predictions
// Official models use /v1/models/{owner}/{name}/predictions
const COMMUNITY_MODEL_VERSIONS: Record<string, string> = {
    "cjwbw/demucs": "25a173108cff36ef9f80f854c162d01df9e6528be175794b81158fa03836d953",
    "rhelsing/basic-pitch": "a7cf33cf63fca9c71f2235332af5a9fdfb7d23c459a0dc429daa203ff8e80c78",
    "google/lyria-2": "6090d04cd804c79b411fb5363d8b816d84604adf119af49c681bb91f09a741e0",
    "elevenlabs/music": "6964c73187da2ba9e640d7d4ff867ea5f963d71a225a5947009e53202c23db9f"
}

function replicateModelUrl(model: string): string {
    const ownerName = model.split(":")[0]
    if (COMMUNITY_MODEL_VERSIONS[ownerName]) {
        return `${REPLICATE_PROXY}/predictions`
    }
    return `${REPLICATE_PROXY}/models/${ownerName}/predictions`
}

function replicateBody(model: string, input: Record<string, unknown>): string {
    const ownerName = model.split(":")[0]
    const version = COMMUNITY_MODEL_VERSIONS[ownerName]
    if (version) {
        return JSON.stringify({version, input})
    }
    return JSON.stringify({input})
}

function parseReplicateError(status: number, body: string): string {
    if (status === 429) return "Replicate rate limit reached. Please wait a moment and try again."
    try { return JSON.parse(body).error ?? `Request failed (${status})` } catch { return body || `Request failed (${status})` }
}

async function replicateCreatePrediction(model: string, input: Record<string, unknown>): Promise<string> {
    const token = await getDailyToken()
    const res = await fetch(replicateModelUrl(model), {
        method: "POST",
        headers: {"Content-Type": "application/json", "Prefer": "wait", ...(token ? {"X-Credit-Token": token} : {})},
        body: replicateBody(model, input)
    })
    if (!res.ok) throw new Error(parseReplicateError(res.status, await res.text()))
    const data = await res.json()
    if (data.status === "succeeded") return typeof data.output === "string" ? data.output : data.output[0]
    if (data.status === "failed") throw new Error(`Prediction failed: ${data.error}`)
    return await replicatePollPrediction(data.id)
}

async function replicatePollPrediction(id: string): Promise<string> {
    for (let i = 0; i < 300; i++) {
        await new Promise(r => setTimeout(r, 2000))
        const res = await fetch(`${REPLICATE_PROXY}/predictions/${id}`)
        if (!res.ok) throw new Error(`Poll failed: ${res.status}`)
        const data = await res.json()
        if (data.status === "succeeded") return typeof data.output === "string" ? data.output : data.output[0]
        if (data.status === "failed") throw new Error(`Prediction failed: ${data.error}`)
    }
    throw new Error("Prediction timed out")
}

async function replicateCreateAndPoll(model: string, input: Record<string, unknown>): Promise<any> {
    const token = await getDailyToken()
    const res = await fetch(replicateModelUrl(model), {
        method: "POST",
        headers: {"Content-Type": "application/json", ...(token ? {"X-Credit-Token": token} : {})},
        body: replicateBody(model, input)
    })
    if (!res.ok) throw new Error(parseReplicateError(res.status, await res.text()))
    const data = await res.json()
    if (data.status === "succeeded") return data.output
    if (data.status === "failed") throw new Error(`Prediction failed: ${data.error}`)
    return await replicatePollPredictionFull(data.id)
}

async function replicatePollPredictionFull(id: string): Promise<any> {
    for (let i = 0; i < 300; i++) {
        await new Promise(r => setTimeout(r, 2000))
        const res = await fetch(`${REPLICATE_PROXY}/predictions/${id}`)
        if (!res.ok) throw new Error(`Poll failed: ${res.status}`)
        const data = await res.json()
        if (data.status === "succeeded") return data.output
        if (data.status === "failed") throw new Error(`Prediction failed: ${data.error}`)
    }
    throw new Error("Prediction timed out")
}

export type MusicModel = {
    id: string
    name: string
    description: string
    model: string
}

export const MUSIC_MODELS: MusicModel[] = [
    {
        id: "minimax",
        name: "MiniMax Music",
        description: "Rich instrumentation, 44.1kHz hi-fi, up to 3500 chars",
        model: "minimax/music-2.5"
    },
    {
        id: "lyria2",
        name: "Google Lyria 2",
        description: "Cinematic 48kHz stereo, 30s fixed length",
        model: "google/lyria-2"
    },
    {
        id: "elevenlabs",
        name: "ElevenLabs Music",
        description: "High-quality vocals & instrumentals, up to 60s",
        model: "elevenlabs/music"
    }
]

export async function generateSong(
    modelId: string,
    prompt: string,
    durationSeconds: number,
    bpm: number,
    instrumental: boolean
): Promise<string> {
    const model = MUSIC_MODELS.find(m => m.id === modelId) ?? MUSIC_MODELS[0]
    const fullPrompt = `${prompt}. BPM: ${bpm}${instrumental ? ". Instrumental only, no vocals" : ""}`

    if (model.id === "elevenlabs") {
        const cappedDuration = Math.min(durationSeconds, 60)
        return await replicateCreatePrediction(model.model, {
            prompt: fullPrompt,
            music_length_ms: cappedDuration * 1000,
            force_instrumental: instrumental,
            output_format: "wav_cd_quality"
        })
    } else if (model.id === "lyria2") {
        const negPrompt = instrumental ? "vocals, singing, voice" : ""
        return await replicateCreatePrediction(model.model, {
            prompt: fullPrompt,
            ...(negPrompt ? {negative_prompt: negPrompt} : {})
        })
    } else {
        // MiniMax: use polling (no Prefer:wait) since it exceeds Vercel's 30s function timeout
        const lyrics = instrumental
            ? "[Instrumental]\n(Full instrumental arrangement)"
            : prompt
        const output = await replicateCreateAndPoll(model.model, {
            prompt: fullPrompt,
            lyrics
        })
        return typeof output === "string" ? output : output[0]
    }
}

export type StemResult = {
    vocals: string
    drums: string
    bass: string
    other: string
}

export async function separateStems(audioUrl: string): Promise<StemResult> {
    const output = await replicateCreateAndPoll(
        "cjwbw/demucs",
        {audio: audioUrl, output_format: "wav"}
    )
    return {
        vocals: output.vocals,
        drums: output.drums,
        bass: output.bass,
        other: output.other
    }
}

export async function generateSoundAI(
    prompt: string,
    durationSeconds: number,
    bpm: number
): Promise<string> {
    const fullPrompt = `${prompt}. BPM: ${bpm}. Instrumental only, no vocals`
    const cappedDuration = Math.min(durationSeconds, 60)
    return await replicateCreatePrediction("elevenlabs/music", {
        prompt: fullPrompt,
        music_length_ms: cappedDuration * 1000,
        force_instrumental: true,
        output_format: "wav_cd_quality"
    })
}

export type MidiNote = {
    pitch: number
    position: number
    duration: number
    velocity: number
}

export type MidiTrackData = {
    name: string
    notes: MidiNote[]
    program?: number   // GM program number (0-127)
    channel?: number   // MIDI channel (9 = drums)
}

export type ChatMessage = {
    role: "user" | "assistant"
    content: string
    midiTracks?: MidiTrackData[]
}

// ── MIDI transcription via Replicate (rhelsing/basic-pitch) ──

export async function transcribeAudioToMidi(audioUrl: string): Promise<string> {
    // Returns URL to a .mid file
    return await replicateCreatePrediction(
        "rhelsing/basic-pitch",
        {audio_file: audioUrl}
    )
}

// ── MIDI binary parser ──

const GM_INSTRUMENTS: Record<number, string> = {
    0: "Piano", 1: "Piano", 2: "Electric Piano", 3: "Honky-tonk", 4: "Electric Piano",
    5: "Electric Piano", 6: "Harpsichord", 7: "Clavinet",
    24: "Guitar", 25: "Guitar", 26: "Electric Guitar", 27: "Electric Guitar",
    28: "Electric Guitar", 29: "Overdrive Guitar", 30: "Distortion Guitar", 31: "Guitar Harmonics",
    32: "Bass", 33: "Electric Bass", 34: "Electric Bass", 35: "Fretless Bass",
    36: "Slap Bass", 37: "Slap Bass", 38: "Synth Bass", 39: "Synth Bass",
    40: "Violin", 41: "Viola", 42: "Cello", 43: "Contrabass",
    48: "Strings", 49: "Strings", 50: "Synth Strings", 51: "Synth Strings",
    56: "Trumpet", 57: "Trombone", 58: "Tuba", 59: "Muted Trumpet",
    60: "French Horn", 61: "Brass", 62: "Synth Brass", 63: "Synth Brass",
    64: "Soprano Sax", 65: "Alto Sax", 66: "Tenor Sax", 67: "Baritone Sax",
    73: "Flute", 74: "Recorder", 75: "Pan Flute",
    80: "Synth Lead", 81: "Synth Lead", 88: "Synth Pad", 89: "Synth Pad"
}

function instrumentName(program: number, channel: number): string {
    if (channel === 9) return "Drums"
    return GM_INSTRUMENTS[program] ?? `Track ${program}`
}

export function parseMidiFile(buffer: ArrayBuffer, bpm: number): MidiTrackData[] {
    return parseMidiFileProper(buffer, bpm)
}

function parseMidiFileProper(buffer: ArrayBuffer, _bpm: number): MidiTrackData[] {
    const view = new DataView(buffer)
    let offset = 0

    const readU16 = () => { const v = view.getUint16(offset); offset += 2; return v }
    const readU32 = () => { const v = view.getUint32(offset); offset += 4; return v }
    const len = buffer.byteLength
    const readVarLen = () => {
        let val = 0
        for (let i = 0; i < 4; i++) {
            if (offset >= len) break
            const b = view.getUint8(offset++)
            val = (val << 7) | (b & 0x7F)
            if (!(b & 0x80)) break
        }
        return val
    }

    // Header
    readU32() // MThd
    const headerLen = readU32()
    readU16() // format
    const numTracks = readU16()
    const ticksPerQN = readU16()
    offset = 8 + headerLen

    // Collect events per channel
    type NoteEvent = {pitch: number, position: number, duration: number, velocity: number}
    const channelPrograms: Map<number, number> = new Map()
    const channelNotes: Map<number, NoteEvent[]> = new Map()

    for (let t = 0; t < numTracks; t++) {
        readU32() // MTrk
        const trackLen = readU32()
        const trackEnd = offset + trackLen
        let runningStatus = 0
        let absoluteTick = 0
        const pending: Map<number, {tick: number, velocity: number}> = new Map()

        while (offset < trackEnd && offset < len) {
            const delta = readVarLen()
            absoluteTick += delta
            if (offset >= len) break

            let byte = view.getUint8(offset)
            if (byte & 0x80) {
                runningStatus = byte
                offset++
            } else {
                byte = runningStatus
            }

            const type = byte & 0xF0
            const channel = byte & 0x0F

            if (type === 0x90 || type === 0x80) {
                const pitch = view.getUint8(offset++)
                const velocity = view.getUint8(offset++)
                const noteKey = channel * 128 + pitch

                if (type === 0x90 && velocity > 0) {
                    pending.set(noteKey, {tick: absoluteTick, velocity: velocity / 127})
                    if (!channelNotes.has(channel)) channelNotes.set(channel, [])
                } else {
                    const start = pending.get(noteKey)
                    if (start) {
                        const posBeats = start.tick / ticksPerQN
                        const durBeats = (absoluteTick - start.tick) / ticksPerQN
                        if (!channelNotes.has(channel)) channelNotes.set(channel, [])
                        channelNotes.get(channel)!.push({
                            pitch,
                            position: posBeats,
                            duration: Math.max(durBeats, 0.01),
                            velocity: start.velocity
                        })
                        pending.delete(noteKey)
                    }
                }
            } else if (type === 0xC0) {
                const program = view.getUint8(offset++)
                channelPrograms.set(channel, program)
            } else if (type === 0xD0) {
                offset += 1
            } else if (type === 0xB0 || type === 0xA0 || type === 0xE0) {
                offset += 2
            } else if (byte === 0xFF) {
                offset++ // meta type
                const metaLen = readVarLen()
                offset += metaLen
            } else if (byte === 0xF0 || byte === 0xF7) {
                const sysLen = readVarLen()
                offset += sysLen
            }
        }
        offset = trackEnd
    }

    // Convert to MidiTrackData[]
    const result: MidiTrackData[] = []
    for (const [channel, notes] of channelNotes.entries()) {
        if (notes.length === 0) continue
        const program = channelPrograms.get(channel) ?? 0
        const name = instrumentName(program, channel)
        result.push({name, notes, program, channel})
    }
    return result
}

// ── Agent pipeline: generate song -> split stems -> transcribe each to MIDI ──

export type AgentStepCallback = (step: string) => void

export async function agentGenerateAndTranscribe(
    prompt: string,
    bpm: number,
    onStep: AgentStepCallback
): Promise<{stems: Record<string, {audioUrl: string, midiUrl: string, tracks: MidiTrackData[]}>, message: string}> {
    // Step 1: Generate song with ElevenLabs at the workspace BPM
    onStep("Generating song with ElevenLabs...")
    const audioUrl = await generateSong("elevenlabs", prompt, 30, bpm, true)

    // Step 2: Separate stems
    onStep("Separating stems (vocals, drums, bass, other)...")
    const stemResult = await separateStems(audioUrl)

    // Step 3: Transcribe each stem to MIDI in parallel
    const stemEntries = Object.entries(stemResult) as [string, string][]
    const results: Record<string, {audioUrl: string, midiUrl: string, tracks: MidiTrackData[]}> = {}

    const transcribePromises = stemEntries.map(async ([stemName, stemUrl]) => {
        onStep(`Transcribing ${stemName} to MIDI...`)
        try {
            const midiUrl = await transcribeAudioToMidi(stemUrl)
            // Download and parse the MIDI file
            const midiBuffer = await fetchAudioAsArrayBuffer(midiUrl)
            const tracks = parseMidiFileProper(midiBuffer, bpm)
            results[stemName] = {audioUrl: stemUrl, midiUrl, tracks}
            onStep(`${stemName}: ${tracks.length} track(s) transcribed`)
        } catch (e: any) {
            console.warn(`Failed to transcribe ${stemName}:`, e)
            results[stemName] = {audioUrl: stemUrl, midiUrl: "", tracks: []}
            onStep(`${stemName}: transcription failed (${e.message})`)
        }
    })

    await Promise.all(transcribePromises)

    const totalTracks = Object.values(results).reduce((sum, r) => sum + r.tracks.length, 0)
    const message = `Generated song, split into ${stemEntries.length} stems, and transcribed ${totalTracks} MIDI track(s). Each stem has been converted to MIDI and added to the timeline.`

    return {stems: results, message}
}

// ── Chat via Replicate GPT-5 ──

export async function chatAboutMusicGPT5(
    prompt: string,
    history: ChatMessage[],
    midiContext?: MidiTrackData[]
): Promise<string> {
    // Build context about current MIDI tracks if available
    let midiSummary = ""
    if (midiContext && midiContext.length > 0) {
        midiSummary = "\n\nCurrent MIDI tracks in the project:\n"
        for (const track of midiContext) {
            const pitchRange = track.notes.length > 0
                ? `notes ${Math.min(...track.notes.map(n => n.pitch))}-${Math.max(...track.notes.map(n => n.pitch))}`
                : "empty"
            const beats = track.notes.length > 0
                ? Math.max(...track.notes.map(n => n.position + n.duration)).toFixed(1)
                : "0"
            midiSummary += `- ${track.name}: ${track.notes.length} notes, ${pitchRange}, ${beats} beats long\n`
        }
    }

    const systemPrompt = `You are an expert music producer and advisor with deep knowledge of MIDI, music theory, arrangement, and production. Help the user with questions about their music, suggest improvements, analyze their MIDI tracks, and provide creative guidance. Be concise and practical.${midiSummary}`

    const input = `${systemPrompt}\n\n${history.map(m => `${m.role === "user" ? "User" : "Assistant"}: ${m.content}`).join("\n")}\nUser: ${prompt}\nAssistant:`

    const res = await fetch(replicateModelUrl("openai/gpt-5"), {
        method: "POST",
        headers: {"Content-Type": "application/json", "Prefer": "wait"},
        body: JSON.stringify({
            input: {
                prompt: input,
                max_tokens: 2048
            }
        })
    })

    if (!res.ok) throw new Error(`GPT-5 error: ${res.status} ${await res.text()}`)
    const data = await res.json()

    if (data.status === "succeeded") {
        // Output is typically an array of string tokens or a single string
        const output = data.output
        if (Array.isArray(output)) return output.join("")
        return String(output)
    }
    if (data.status === "failed") throw new Error(`GPT-5 failed: ${data.error}`)

    // Poll if not immediately ready
    const result = await replicatePollPredictionFull(data.id)
    if (Array.isArray(result)) return result.join("")
    return String(result)
}

// ── Legacy MIDI generation (kept for backwards compat, no longer used by agent) ──

export async function generateMIDI(
    prompt: string,
    bpm: number,
    history: ChatMessage[]
): Promise<{message: string, tracks: MidiTrackData[]}> {
    const systemPrompt = `You are an expert music producer and MIDI composer. When asked to create music, respond with MIDI data in JSON format.

ALWAYS respond with a JSON block wrapped in \`\`\`json ... \`\`\` containing an array of tracks. Each track has:
- "name": instrument name (e.g. "Piano", "Bass", "Drums", "Melody")
- "notes": array of note objects with:
  - "pitch": MIDI note number (0-127, where 60=C4, 64=E4, 67=G4)
  - "position": position in beats (0 = start, 1 = beat 2, etc.)
  - "duration": duration in beats
  - "velocity": 0.0 to 1.0

The BPM is ${bpm}. Position and duration are in beats (quarter notes).
Use musically accurate note values. A bar = 4 beats in 4/4 time.

After the JSON, you may add a brief text explanation of the music.`

    const messages = [
        {role: "system" as const, content: systemPrompt},
        ...history.map(m => ({role: m.role as "user" | "assistant", content: m.content})),
        {role: "user" as const, content: prompt}
    ]

    const openaiToken = await getDailyToken()
    const res = await fetch("/api/openai/chat/completions", {
        method: "POST",
        headers: {"Content-Type": "application/json", ...(openaiToken ? {"X-Credit-Token": openaiToken} : {})},
        body: JSON.stringify({
            model: "gpt-4o",
            messages,
            temperature: 0.7,
            max_tokens: 4096
        })
    })

    if (!res.ok) throw new Error(`OpenAI error: ${res.status} ${await res.text()}`)
    const data = await res.json()
    const content = data.choices[0].message.content

    const tracks: MidiTrackData[] = []
    const jsonMatch = content.match(/```json\s*([\s\S]*?)\s*```/)
    if (jsonMatch) {
        try {
            const parsed = JSON.parse(jsonMatch[1])
            if (Array.isArray(parsed)) {
                tracks.push(...parsed)
            }
        } catch (e) {
            console.warn("Failed to parse MIDI JSON:", e)
        }
    }

    const textParts = content.replace(/```json[\s\S]*?```/g, "").trim()
    return {message: textParts || "Here's your MIDI composition.", tracks}
}

export async function chatAboutMusic(
    prompt: string,
    history: ChatMessage[]
): Promise<string> {
    const systemPrompt = `You are an expert music producer and advisor. Help the user with music production questions, theory, arrangement ideas, and creative suggestions. Do NOT generate MIDI data - just provide helpful text advice and discussion.`

    const messages = [
        {role: "system" as const, content: systemPrompt},
        ...history.map(m => ({role: m.role as "user" | "assistant", content: m.content})),
        {role: "user" as const, content: prompt}
    ]

    const chatToken = await getDailyToken()
    const res = await fetch("/api/openai/chat/completions", {
        method: "POST",
        headers: {"Content-Type": "application/json", ...(chatToken ? {"X-Credit-Token": chatToken} : {})},
        body: JSON.stringify({
            model: "gpt-4o",
            messages,
            temperature: 0.7,
            max_tokens: 2048
        })
    })

    if (!res.ok) throw new Error(`OpenAI error: ${res.status} ${await res.text()}`)
    const data = await res.json()
    return data.choices[0].message.content
}

export async function fetchAudioAsArrayBuffer(url: string): Promise<ArrayBuffer> {
    const res = await fetch(url)
    if (!res.ok) throw new Error(`Failed to fetch audio: ${res.status}`)
    return await res.arrayBuffer()
}
